/**
 * @file: usePreInspectionReportActions.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { AppNavParams } from '@euler/app/Routes';
import type { TaskNavParams } from '@euler/app/flows/task/TaskFlow';
import { useTaskContext } from '@euler/app/flows/task/functions/TaskContext';
import { useViewReportWithTaskContext } from '@euler/app/flows/task/functions/useViewReportWithTaskContext';
import { useAppLoading } from '@euler/components/loading/Provider';
import { OrgUserRoleHelper } from '@euler/functions/OrgUserRoleHelper';
import { useAlert } from '@euler/functions/ux/useAlert';
import { useConfirm } from '@euler/functions/ux/useConfirm';
import { HttpError } from '@euler/lib/request';
import { delayed } from '@euler/utils';
import { usePersistFn } from '@euler/utils/hooks';
import { useActionSheet } from '@expo/react-native-action-sheet';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { RefObject, useEffect } from 'react';
import { View, findNodeHandle } from 'react-native';
import { useFacadeSiteRequirements } from './useFacadeSiteRequirements';
import { usePreInspectionUserPicker } from './usePreInspectionUserPicker';

export const usePreInspectionActions = ({ strict }: { strict: boolean }) => {
  const alert = useAlert();
  const confirm = useConfirm();

  const navigation =
    useNavigation<StackNavigationProp<AppNavParams & TaskNavParams>>();

  const { taskManager, detail, storeConfiguration, profile } = useTaskContext();

  const { user, ...userPickerInfo } = usePreInspectionUserPicker();

  const appLoading = useAppLoading();

  const { getUncheckedRequiredSites } = useFacadeSiteRequirements();

  const validate = usePersistFn((options: { throwErrorOnInvalid: boolean }) => {
    __DEV__ && console.log('validating... ');
    let errorMsg: string | undefined = undefined;
    if (!detail.preInspection.dashboardImgUrl) {
      errorMsg =
        '对不起，请确保已经成功上传了仪表盘照片，然后才能完成并推送报告!';
    } else if (!taskManager.preInspectionManager.canFinish()) {
      errorMsg = '有环检图片/视频正在上传，请稍候!';
    }
    __DEV__ && console.log('validate error: ', errorMsg);
    if (errorMsg) {
      if (options.throwErrorOnInvalid) {
        throw new Error(errorMsg);
      } else {
        void alert({ title: '提示', message: errorMsg });
        return false;
      }
    }
    return true;
  });

  // gates the report preview's finish button. the required sites configured on
  // the facade template are only enforced here - not when previewing - so that
  // the report stays previewable while the pre-inspection is still being filled
  // in. runs after `validate` so that an upload still in flight reports itself
  // rather than looking like a missing check.
  const validateFinish = usePersistFn(() => {
    if (!validate({ throwErrorOnInvalid: false })) return false;

    const uncheckedSites = getUncheckedRequiredSites();
    if (!uncheckedSites.length) return true;

    void alert({
      title: '提示',
      message: `以下必检部位尚未拍照记录，请完成后再完成并推送报告: ${uncheckedSites
        .map(x => x.name)
        .join('、')}`,
    });
    return false;
  });

  const onFinish = usePersistFn(async () => {
    if (validate({ throwErrorOnInvalid: true })) {
      try {
        await taskManager.preInspectionManager.finish({
          technicianId: user.id,
          technicianName: user.name,
        });
      } catch (e) {
        const error = e as HttpError;
        console.error('failed to finish pre-inspection: ', error);
        // By subCode, not message: the message is localized copy.
        if (error.subCode === 'task_inspection_flow_not_found') {
          void alert({ title: '未找到任务检查流程' });
        } else {
          void alert({ title: '出错了!', message: error.message });
        }
      }
    }
  });

  const onCancel = usePersistFn(async () => {
    if (storeConfiguration.restrictedRolesForCancelPreInspection?.length) {
      const roleHelper = new OrgUserRoleHelper(profile.user.role);
      if (
        !roleHelper.matchesAny(
          storeConfiguration.restrictedRolesForCancelPreInspection,
        )
      ) {
        void alert({
          title: '您没有权限取消预检',
          message: '若需帮助，请联系门店系统管理员！',
        });
        return;
      }
    }

    if (
      !(await confirm({
        title: '取消预检确认',
        message:
          '您即将取消当前车辆的预检项目，该操作不可撤销，是否确认继续取消?',
      }))
    ) {
      return;
    }

    delayed(async () => {
      await appLoading.showWithAction(async () => {
        await taskManager.preInspectionManager.cancel();
      });
      appLoading.show({
        type: 'success',
        message: '已取消',
      });
    }, 350);

    navigation.goBack();
  });

  const viewReport = useViewReportWithTaskContext();

  const onViewReport = usePersistFn(() => {
    if (strict && !validate({ throwErrorOnInvalid: false })) {
      return;
    }
    viewReport('preInspectionReport', {
      onFinish,
      validateFinish,
    });
  });

  const { showActionSheetWithOptions } = useActionSheet();

  const showActions = usePersistFn((anchor?: RefObject<View>) => {
    let nodeId: number | null = null;
    if (anchor?.current != null) {
      nodeId = findNodeHandle(anchor.current);
    }
    showActionSheetWithOptions(
      {
        options: ['取消接车预检', '关闭'],
        anchor: nodeId ?? undefined,
        destructiveButtonIndex: 0,
        cancelButtonIndex: 1,
      },
      async buttonIndex => {
        if (buttonIndex === 0) {
          void onCancel();
        }
      },
    );
  });

  useEffect(() => {
    void taskManager.sync({
      callback: (_err, resp) => {
        if (resp != null && 'preInspection' in resp.response) {
          taskManager.preInspectionManager.resetSiteInspectionBundle(
            'dashboard',
          );
          taskManager.preInspectionManager.resetSiteInspectionBundle('facade');
        }
      },
    });
  }, [taskManager]);

  return {
    detail,
    onFinish,
    onCancel,
    onViewReport,
    showActions,
    user,
    userPickerInfo,
  };
};
